/**
 * Pure camera-math helpers + zoom/pan constants extracted from
 * `stageInteractions.ts` (cmd-pan r5 — keep that file under the 2000-line cap).
 *
 * Everything here is framework-agnostic and free of any `installStageInteractions`
 * closure state: plain functions over a THREE.OrthographicCamera + a canvas rect.
 * `stageInteractions.ts` re-exports the whole surface, so external import paths
 * (`from "../stageInteractions"`) are unchanged — this is the project's standard
 * "split into a sibling + barrel re-export" pattern, no behaviour change.
 */

import * as THREE from "three";

/** LMB-drag threshold (CSS px) before we switch from click to rubber band. */
export const DRAG_THRESHOLD_PX = 4;

/**
 * AutoCAD `ZOOMFACTOR` system variable default. Range 3..100. Drives the
 * per-notch wheel zoom factor via `factor = 1 + ZOOMFACTOR / 100`.
 *
 * Reference: Autodesk knowledge base ZOOMFACTOR (2025) — default 60,
 * documented invariant "wheel forward N notches then back N notches
 * leaves the view unchanged" implies the multiplicative symmetric form
 * `factor_in = 1 + ZF/100`, `factor_out = 1 / factor_in`. With ZF=60 →
 * 1.6× in / 0.625× out per notch.
 *
 * Browser wheel `deltaY` is +100 per notch down (zoom out), -100 per
 * notch up (zoom in). We treat 100 deltaY units as one AutoCAD notch.
 */
export const ZOOMFACTOR_DEFAULT = 60;

/**
 * Pixels of wheel `deltaY` that correspond to one AutoCAD ZOOMFACTOR
 * notch. Most browsers emit ±100 per detent; trackpad scroll emits
 * smaller fractional deltas which are scaled proportionally so the
 * total zoom over a finger swipe matches the swipe extent.
 */
export const WHEEL_NOTCH_DELTA = 100;

/**
 * Legacy pre-fix wheel coefficient kept as documentation only. The
 * formula `exp(-deltaY * 0.001)` produced ≈ 1.105× per notch (10.5%),
 * far below the AutoCAD baseline of 1.6× per notch (60%). Replaced by
 * the ZOOMFACTOR-driven path in `wheelDeltaToZoomFactor`. Retained as
 * a named constant only so legacy unit tests can still reference it
 * for documentation purposes — production code reads `ZOOMFACTOR_DEFAULT`.
 *
 * @deprecated use `ZOOMFACTOR_DEFAULT` and the ratio formula instead.
 */
export const WHEEL_RATE = 0.001;

/** Hard zoom clamps so a runaway scroll can't poison the projection. */
export const MIN_ZOOM = 1e-6;
export const MAX_ZOOM = 1e6;

/**
 * Convert a `clientX/Y` (window coords) to world space using the
 * supplied `OrthographicCamera`. Mirrors the per-canvas math
 * `SceneContent.clientToWorld` already uses for selection.
 */
export function clientToWorld(
  clientX: number,
  clientY: number,
  canvas: HTMLElement,
  camera: THREE.Camera,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const ndcX = ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
  const ndcY = -(((clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1);
  const v = new THREE.Vector3(ndcX, ndcY, 0).unproject(camera);
  return { x: v.x, y: v.y };
}

/**
 * Exact inverse of `clientToWorld`: convert a world-space point (the same
 * render/offset-world frame `clientToWorld` returns) back to `clientX/clientY`
 * (window coords) using the supplied `OrthographicCamera`. Projects the point
 * to NDC via `camera.project()` then maps NDC onto the canvas's own
 * `getBoundingClientRect()` rect — the mirror image of `clientToWorld`'s
 * `unproject()` + rect math, so `worldToClient(...clientToWorld(cx, cy, ...))`
 * round-trips to `(cx, cy)` for any camera pose.
 *
 * Added to give `SceneBridgeProjection.clientFromWorld`
 * (`scene/useStageRendererHandle.ts`) a REAL projection instead of the
 * identity stub it shipped with (PR-C TODO, tracked since commit
 * ba2271685): `window.__edit_dwg_scene.entityBounds()` is documented to
 * return client-space pixels, but with the identity stub it silently
 * returned raw world units whenever the camera wasn't at zoom=1 / centred
 * on the canvas origin — i.e. under every non-trivial camera, which is the
 * normal product state and the abnormal test state.
 */
export function worldToClient(
  worldX: number,
  worldY: number,
  canvas: HTMLElement,
  camera: THREE.Camera,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const ndc = new THREE.Vector3(worldX, worldY, 0).project(camera);
  return {
    x: rect.left + ((ndc.x + 1) / 2) * Math.max(rect.width, 1),
    y: rect.top + ((1 - ndc.y) / 2) * Math.max(rect.height, 1),
  };
}

/**
 * AutoCAD PICKBOX-equivalent aperture, in CSS pixels, both the production
 * click pipeline (`StagePointerHandlers.PICK_APERTURE_PX`) and the
 * `__edit_dwg_scene` test bridge (`entityIdAt`'s `worldTolerance()`) hit-test
 * against. Single source so the two can never drift out of lock-step (they
 * used to be two separately-hardcoded `4`s — see #589 Codex review).
 */
export const PICK_APERTURE_PX = 4;

/**
 * World-space span of one CSS pixel at the camera's current zoom (how many
 * world units one on-screen pixel covers). `null` for a non-orthographic
 * camera (this app only ever uses orthographic, but callers already guarded
 * for it before this was extracted).
 */
export function orthoPixelToWorld(
  camera: THREE.Camera,
  canvasWidthPx: number,
): number | null {
  const ortho = camera as THREE.OrthographicCamera;
  if (!ortho.isOrthographicCamera) return null;
  const worldWidth = (ortho.right - ortho.left) / ortho.zoom;
  return worldWidth / Math.max(canvasWidthPx, 1);
}

/**
 * World-space radius of an `apertureCssPx`-wide pick aperture — the
 * tolerance `pick()`/`SpatialIndex.intersect()` hit-test against. Extracted
 * from `StagePointerHandlers.pickToleranceWorld` (#589 Codex review: the
 * test bridge's `worldTolerance()` used a bare `1/zoom`, ignoring canvas
 * size and frustum width entirely — after a fit-to-extents on a large
 * drawing, `zoom` can be ~1 while the frustum spans thousands of world
 * units, so `1/zoom` searched a ~1-world-unit box where a real click's
 * 4-CSS-px aperture would cover far more). Falls back to 1 world unit for a
 * non-orthographic camera, matching the pre-extraction fallback.
 */
export function pickApertureWorldTolerance(
  camera: THREE.Camera,
  canvasWidthPx: number,
  apertureCssPx: number,
): number {
  const pixelToWorld = orthoPixelToWorld(camera, canvasWidthPx);
  return pixelToWorld === null ? 1 : apertureCssPx * pixelToWorld;
}

/**
 * Convert wheel `deltaY` → multiplicative zoom factor.
 *
 * AutoCAD ZOOMFACTOR parity: the per-notch ratio is
 * `r = 1 + zoomFactor/100` (default 1.6× at ZF=60). For each browser
 * "notch" (`WHEEL_NOTCH_DELTA` of `deltaY`) we multiply by `1/r` on
 * scroll-down (zoom out, deltaY > 0) or by `r` on scroll-up (zoom in,
 * deltaY < 0). Fractional notches (trackpad inertial scroll) compose
 * via `r ** notches` so a continuous swipe zooms smoothly.
 *
 * Invariant: `wheelDeltaToZoomFactor(d) * wheelDeltaToZoomFactor(-d) ≈ 1`
 * — N notches in then N notches out leaves the view unchanged, matching
 * the AutoCAD documented behaviour.
 *
 * Pure function — no THREE / DOM access — easy to unit-test.
 */
export function wheelDeltaToZoomFactor(
  deltaY: number,
  zoomFactor: number = ZOOMFACTOR_DEFAULT,
): number {
  if (deltaY === 0) return 1;
  // Clamp the configured ZF into the AutoCAD-documented 3..100 range
  // so a runaway value can't poison the curve. Falls back to the
  // default if the input is non-finite.
  const zf = Number.isFinite(zoomFactor)
    ? Math.max(3, Math.min(100, zoomFactor))
    : ZOOMFACTOR_DEFAULT;
  const ratio = 1 + zf / 100;
  // Positive deltaY = wheel down = zoom out (factor < 1).
  // Negative deltaY = wheel up   = zoom in  (factor > 1).
  const notches = deltaY / WHEEL_NOTCH_DELTA;
  return Math.pow(ratio, -notches);
}

/**
 * Apply a zoom factor while keeping `worldUnderCursor` pinned to the
 * cursor. Returns the new camera state (centre + ortho.zoom).
 *
 * `worldUnderCursor` is captured **before** the zoom change. After
 * the zoom mutation, the camera centre is shifted so the cursor
 * still projects to the same world point.
 */
export function zoomAroundCursor(
  ortho: THREE.OrthographicCamera,
  factor: number,
  cursorClient: { x: number; y: number },
  canvas: HTMLElement,
): void {
  // Three.js `unproject` reads `camera.matrixWorld` (built from
  // `position` + `quaternion`) — so we must call `updateMatrixWorld`
  // before / after we read or mutate the position. `updateProjection-
  // Matrix` only refreshes the frustum / zoom side; without the
  // matrixWorld refresh, `unproject` will project against the stale
  // pre-mutation pose and the cursor-pinning math drifts by the pan
  // amount.
  ortho.updateMatrixWorld();
  const worldBefore = clientToWorld(
    cursorClient.x,
    cursorClient.y,
    canvas,
    ortho,
  );
  const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, ortho.zoom * factor));
  ortho.zoom = newZoom;
  ortho.updateProjectionMatrix();
  const worldAfter = clientToWorld(
    cursorClient.x,
    cursorClient.y,
    canvas,
    ortho,
  );
  ortho.position.x += worldBefore.x - worldAfter.x;
  ortho.position.y += worldBefore.y - worldAfter.y;
  ortho.updateMatrixWorld();
  ortho.updateProjectionMatrix();
}

/**
 * Pan camera by a screen-space pixel delta. CAD Y-up → invert `dy`
 * sign so the world tracks the natural drag direction.
 */
export function panByPixels(
  ortho: THREE.OrthographicCamera,
  size: { width: number; height: number },
  dxPx: number,
  dyPx: number,
  startCenter: { x: number; y: number },
): void {
  const worldPerPxX =
    (ortho.right - ortho.left) / (Math.max(ortho.zoom, MIN_ZOOM) * Math.max(size.width, 1));
  const worldPerPxY =
    (ortho.top - ortho.bottom) /
    (Math.max(ortho.zoom, MIN_ZOOM) * Math.max(size.height, 1));
  ortho.position.x = startCenter.x - dxPx * worldPerPxX;
  ortho.position.y = startCenter.y + dyPx * worldPerPxY;
  ortho.updateMatrixWorld();
  ortho.updateProjectionMatrix();
}
